import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ENV, Env } from '@bodyanalytics/app-config';
import { Observable, catchError, tap } from 'rxjs';
import { SharedErrorService } from '../services/error/error.service';
import { SharedLogService } from '../services/log/log.service';

/** Per-call options shared by the CRUD primitives below. */
export interface HttpCallOptions<R> {
  /** Telemetry label forwarded to {@link SharedErrorService.handleError}. */
  op: string;
  /**
   * Logged via {@link SharedLogService} on success. Pass a string for a static
   * message, or a function of the response for messages that read the result
   * (e.g. `created => \`added w/ id=${created.id}\``). Omit to log nothing.
   */
  log?: string | ((response: R) => string);
  /**
   * Value emitted on error instead of rethrowing — e.g. `[]` for list calls.
   * When omitted, `handleError` is called with the op only (single-arg), matching
   * the hand-written services this base replaces.
   *
   * IMPORTANT (#1747): since the fallback contract is now honored, only pass a
   * fallback when every consumer genuinely wants graceful degradation (an empty
   * list/safe default is a valid render). If any consumer needs to distinguish
   * "failed" from "empty" (error banner, retry affordance — #1252/#1287/#1740),
   * OMIT the fallback so the error propagates to its error branch.
   */
  fallback?: R;
}

/**
 * Base class for the admin/customer domain HTTP services, which all repeated the
 * same shape: inject ENV + HttpClient + the shared log/error services, build a
 * URL from `appConfig.api`, and wrap every call in
 * `.pipe(tap(log), catchError(handleError(op, fallback)))`.
 *
 * Subclasses extend this and expose their own entity-named methods that delegate
 * to {@link getResource}/{@link postResource}/etc. with the exact resource path —
 * the path (and therefore the API contract) stays explicit in each service.
 */
@Injectable()
export abstract class BaseHttpCrudService {
  protected readonly appConfig = inject<Env>(ENV);
  protected readonly http = inject(HttpClient);
  protected readonly log = inject(SharedLogService);
  protected readonly errors = inject(SharedErrorService);

  /** JSON headers reused by write calls. Public so callers/specs can reference it. */
  readonly httpOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  /** Join the configured API base with a resource path (e.g. `manage-regions-api/details/1`). */
  protected apiUrl(path: string): string {
    return `${this.appConfig.api}/${path}`;
  }

  protected getResource<R>(path: string, options: HttpCallOptions<R>): Observable<R> {
    return this.http.get<R>(this.apiUrl(path)).pipe(
      tap<R>(response => this.logSuccess(options.log, response)),
      catchError(this.errorHandler<R>(options)),
    );
  }

  protected postResource<R>(path: string, body: unknown, options: HttpCallOptions<R>): Observable<R> {
    return this.http.post<R>(this.apiUrl(path), body, this.httpOptions).pipe(
      tap<R>(response => this.logSuccess(options.log, response)),
      catchError(this.errorHandler<R>(options)),
    );
  }

  protected patchResource<R>(path: string, body: unknown, options: HttpCallOptions<R>): Observable<R> {
    return this.http.patch<R>(this.apiUrl(path), body, this.httpOptions).pipe(
      tap<R>(response => this.logSuccess(options.log, response)),
      catchError(this.errorHandler<R>(options)),
    );
  }

  protected deleteResource<R>(path: string, options: HttpCallOptions<R>): Observable<R> {
    return this.http.delete<R>(this.apiUrl(path), this.httpOptions).pipe(
      tap<R>(response => this.logSuccess(options.log, response)),
      catchError(this.errorHandler<R>(options)),
    );
  }

  private logSuccess<R>(log: HttpCallOptions<R>['log'], response: R): void {
    if (log === undefined) {
      return;
    }
    this.log.log(typeof log === 'function' ? log(response) : log);
  }

  /**
   * Faithfully mirrors the original call sites: `handleError(op)` when there is no
   * fallback, `handleError(op, fallback)` when there is. (The arity matters —
   * some specs assert the single-argument form.)
   */
  private errorHandler<R>(options: HttpCallOptions<R>): (error: unknown) => Observable<R> {
    return options.fallback === undefined
      ? this.errors.handleError<R>(options.op)
      : this.errors.handleError<R>(options.op, options.fallback);
  }
}
